import {getHttpClient} from '../../modules/httpClient.js';
import {Notification} from '../../modules/notification.js';
import {Spinner} from '../../modules/spinner.js';
import {getRandomQuote} from '../../pages/courses/courseLessonItem__quizzes.js';
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {animateHeight} from '../../modules/motion/height.js';
import {settle} from '../../modules/motion/settle.js';
import {t} from '../../localization/translate.js';

const client = getHttpClient();

/** @type {number} Animation duration in milliseconds */
const ANIMATION_DURATION = 400;

/** @type {number} Stagger delay for staged animations */
const STAGGER_DELAY = 100;

/**
 * @param {HTMLElement} container
 */
function initializeQuizArea(container) {
    const quizContainer = container.querySelector('.promotionalQuiz__container');
    const carousel = quizContainer.querySelector('.promotionalQuiz__quizCarousel');
    const carouselItems = carousel.querySelectorAll('.promotionalQuiz__quizCarouselItem');
    const quizArea = quizContainer.querySelector('.promotionalQuiz__quizArea');
    const quizForm = quizArea.querySelector('form');
    const quizCompleteArea = container.querySelector('.promotionalQuiz__completeArea');
    const quizSuccessArea = quizCompleteArea.querySelector('.promotionalQuiz__success');
    const emailSentArea = quizCompleteArea.querySelector('.promotionalQuiz__emailSent');
    const emailForm = quizSuccessArea.querySelector('form');
    const quizFailureArea = quizCompleteArea.querySelector('.promotionalQuiz__failure');
    const stepperContainer = container.querySelector('.js-stepperContainer');
    const steppers = stepperContainer?.querySelectorAll('.js-stepper');

    emailForm.addEventListener('submit', function (submitEvent) {
        submitEvent.preventDefault();
        const formData = new FormData(emailForm);
        submitEmail(formData.get('email'));
    });

    /** @param {boolean} isEnabled */
    function enableLostProgressWarning(isEnabled) {
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        window.onbeforeunload = isEnabled === true
            ? () => t('If you leave the page your progress will be lost.')
            : null;
    }

    /**
     * @param {HTMLElement} container
     * @param {() => void} onSubmit
     */
    function initializeQuiz(container, onSubmit) {
        const submitButton = container.querySelector('.js-quizSubmitButton');
        const inputs = container.querySelectorAll('.js-answerChoice');

        quizCompleteArea.classList.add('hidden');

        submitButton.disabled = true;

        submitButton.addEventListener('click', (event) => {
            event.preventDefault();
            onSubmit();
        });

        /**
         *
         */
        function handleChangeEvent() {
            submitButton.disabled = false;
            inputs.forEach((input) => input.removeEventListener('change', handleChangeEvent));
        }

        inputs.forEach((input) => input.addEventListener('change', handleChangeEvent));
    }

    /**
     * @param {string|number} correct
     * @param {number} total
     */
    function initializeFailureArea(correct, total) {
        quizSuccessArea.hidden = true;

        const correctCountElement = quizFailureArea.querySelector('.js-correct-count');
        const totalCountElement = quizFailureArea.querySelector('.js-total-count');
        const quoteElement = quizFailureArea.querySelector('.js-quote');

        correctCountElement.textContent = correct;
        totalCountElement.textContent = total;

        quoteElement.innerHTML = getRandomQuote().outerHTML;

        quizFailureArea.classList.remove('hidden');
    }

    /**
     * @param {string|number} correct
     * @param {string|number} total
     */
    function initializeSuccessArea(correct, total) {
        quizFailureArea.hidden = true;

        const correctCountElement = quizSuccessArea.querySelector('.js-correct-count');
        const totalCountElement = quizSuccessArea.querySelector('.js-total-count');

        correctCountElement.textContent = correct;
        totalCountElement.textContent = total;

        quizSuccessArea.classList.remove('hidden');
    }

    /**
     * Update the horizontal stepper to show the specified step
     * @param {number} stepNumber The step number to show (1-based)
     */
    function updateStepper(stepNumber) {
        if (!steppers) {
            return;
        }

        steppers.forEach((stepper) => {
            const step = Number.parseInt(stepper.dataset.step, 10);
            if (step === stepNumber) {
                stepper.style.display = '';
            } else {
                stepper.style.display = 'none';
            }
        });
    }

    /**
     * Initialize and show the quiz completion area (success or failure)
     * @param {boolean} pass Whether the user passed the quiz
     * @param {string|number} correct Number of correct answers
     * @param {string|number} total Total number of questions
     */
    async function initializeQuizCompletionArea(pass, correct, total) {
        quizCompleteArea.classList.add('flex');
        quizCompleteArea.classList.remove('hidden');

        if (pass) {
            initializeSuccessArea(correct, total);
            await animateToCompletionArea(quizSuccessArea.clientHeight);
        } else {
            initializeFailureArea(correct, total);
            await animateToCompletionArea(quizFailureArea.clientHeight);
            enableLostProgressWarning(false);
        }

        quizArea.style.opacity = '0';
        quizArea.style.pointerEvents = 'none';
        quizArea.style.position = 'absolute';
    }

    /**
     * Animate transition from quiz area to completion area
     * @param {number} height Target height for the completion area
     * @returns {Promise<void>}
     */
    function animateToCompletionArea(height) {
        const animation = quizArea.animate(
            {
                opacity: [1, 0],
                transform: ['translateX(0)', 'translateX(-30px)'],
            },
            {duration: ANIMATION_DURATION, easing: 'ease-out'}
        );
        quizCompleteArea.animate({opacity: [0, 1]}, {duration: ANIMATION_DURATION, easing: 'ease-in', fill: 'backwards'});
        animateHeight(quizContainer, quizContainer.clientHeight, height, {duration: ANIMATION_DURATION, easing: 'ease-out'});

        return settle(animation).then(() => {});
    }

    /**
     * Animate transition between quiz questions with modern slide and scale effect
     * @param {number} activeIndex The index of the question to show (0-based)
     * @returns {Promise<void>}
     */
    function animateQuestionTransition(activeIndex) {
        return new Promise((resolve) => {
            const oldQuiz = carouselItems[activeIndex - 1];
            const newQuiz = carouselItems[activeIndex];

            if (!oldQuiz || !newQuiz) {
                resolve();
                return;
            }

            // Update stepper to show next step
            updateStepper(activeIndex + 1);

            // Make old quiz absolute so it doesn't take up space during transition
            oldQuiz.classList.add('absolute', 'inset-0');

            // Animate out the old quiz (fade + slide left + scale down)
            const outAnimation = oldQuiz.animate(
                {
                    opacity: [1, 0],
                    transform: [
                        'translateX(0) scale(1)',
                        'translateX(-60px) scale(0.95)',
                    ],
                    filter: ['blur(0px)', 'blur(2px)'],
                },
                {
                    duration: ANIMATION_DURATION,
                    easing: EASING_CURVES['ease-in-out'],
                }
            );
            // The hiding classes take over from the last keyframe before the next paint, so no forwards fill stays behind.
            settle(outAnimation).then(() => oldQuiz.classList.add('opacity-0', 'pointer-events-none'));

            // Prepare new quiz for animation - remove absolute positioning so it takes up space
            newQuiz.classList.remove('absolute', 'inset-0', 'opacity-0', 'pointer-events-none', 'translate-x-20');
            newQuiz.classList.add('opacity-100', 'pointer-events-auto', 'translate-x-0');

            // Animate in the new quiz with a slight delay for staged effect (fade + slide from right + scale up)
            const inAnimation = newQuiz.animate(
                {
                    opacity: [0, 1],
                    transform: [
                        'translateX(80px) scale(0.95)',
                        'translateX(0) scale(1)',
                    ],
                    filter: ['blur(2px)', 'blur(0px)'],
                },
                {
                    duration: ANIMATION_DURATION,
                    delay: STAGGER_DELAY,
                    easing: EASING_CURVES['ease-out'],
                    fill: 'backwards',
                }
            );

            // Animate carousel height if needed
            const oldHeight = oldQuiz.querySelector('.promotionalQuiz__quizCarouselItemContent')?.clientHeight || 0;
            const newHeight = newQuiz.querySelector('.promotionalQuiz__quizCarouselItemContent')?.clientHeight || 0;

            if (oldHeight !== newHeight) {
                animateHeight(carousel, oldHeight, newHeight, {duration: ANIMATION_DURATION + STAGGER_DELAY, easing: EASING_CURVES['ease-in-out']});
            }

            settle(inAnimation).then(() => resolve());
        });
    }

    /**
     * Set the carousel to show a specific question
     * @param {number} activeIndex The index of the question to show (0-based)
     * @param {boolean} animated Whether to animate the transition
     */
    async function setCarouselState(activeIndex, animated = true) {
        /**
         * If the visitor made progress by answering at least the first question,
         * warn about leaving the page as it may lead to lost progress.
         */
        if (activeIndex === 1) {
            enableLostProgressWarning(true);
        }

        if (animated && activeIndex > 0) {
            await animateQuestionTransition(activeIndex);
        } else {
            // Initial state setup (no animation)
            updateStepper(activeIndex + 1);

            carouselItems.forEach((carouselItem, carouselItemIndex) => {
                if (carouselItemIndex === activeIndex) {
                    carouselItem.classList.remove('absolute', 'inset-0', 'opacity-0', 'pointer-events-none', 'translate-x-20');
                    carouselItem.classList.add('opacity-100', 'pointer-events-auto');
                } else {
                    carouselItem.classList.add('absolute', 'inset-0', 'opacity-0', 'pointer-events-none');
                    carouselItem.classList.remove('opacity-100', 'pointer-events-auto', 'translate-x-0');
                }
            });
        }
    }

    /**
     * Submit all quiz answers and show completion area
     * @returns {Promise<void>}
     */
    async function submitAnswers() {
        // Disable all submit buttons to prevent double submission
        const submitButtons = quizForm.querySelectorAll('.js-quizSubmitButton');
        submitButtons.forEach((button) => {
            button.disabled = true;
            button.classList.add('opacity-50', 'cursor-not-allowed');
        });

        const formData = new FormData(quizForm);
        const answers = {};

        const token = formData.get('_token');

        formData.delete('_token');

        formData.forEach((value, question) => answers[question] = value);

        const {correctCount, questionCount, passed, notificationUrl} = await client.post(quizForm.action, {
            _token: token,
            answers,
        });

        if (passed) {
            emailForm.action = notificationUrl;
        }

        // Update stepper to final step before showing completion
        updateStepper(carouselItems.length + 1);

        initializeQuizCompletionArea(passed, correctCount, questionCount);
    }

    /**
     * @param {string} email
     */
    function initializeEmailConfirmationArea(email) {
        const emailElement = emailSentArea?.querySelector('.js-email');

        if (emailElement) {
            emailElement.textContent = email;
        }

        emailSentArea.classList.remove('hidden');
    }

    /**
     * Animate transition from success area to email sent confirmation
     * @returns {Promise<void>}
     */
    async function animateEmailSubmissionCarousel() {
        emailSentArea.style.visibility = '';

        const animation = quizSuccessArea.animate(
            {
                opacity: [1, 0],
                transform: ['translateX(0)', 'translateX(-30px)'],
            },
            {duration: ANIMATION_DURATION, easing: 'ease-out'}
        );
        emailSentArea.animate(
            {
                opacity: [0, 1],
                transform: ['translateX(30px)', 'translateX(0)'],
            },
            {duration: ANIMATION_DURATION, easing: 'ease-out', fill: 'backwards'}
        );
        animateHeight(quizContainer, quizSuccessArea.clientHeight, emailSentArea.clientHeight, {duration: ANIMATION_DURATION, easing: 'ease-out'});

        return settle(animation).then(() => {
            quizSuccessArea.hidden = true;
        });
    }

    /**
     * @param {string} email
     * @returns {Promise<void>}
     */
    async function submitEmail(email) {
        const spinner = new Spinner(container);
        spinner.start();

        let response = null;

        try {
            response = await client.post(emailForm.action, {
                email,
            });
        } catch (error) {
            if (error.status === 422) {
                quizSuccessArea.querySelector('.js-promotionalQuizzesValidationErrors').textContent = error.errorBody.message || '';
            } else {
                Notification.error(error.message);
            }
        } finally {
            spinner.stop();
        }

        if (response !== null) {
            initializeEmailConfirmationArea(email);
            animateEmailSubmissionCarousel();
        }
    }

    carouselItems.forEach((quiz, index) =>
        initializeQuiz(quiz, async () => {
            if (index < carouselItems.length - 1) {
                setCarouselState(index + 1);
            } else {
                submitAnswers();
            }
        })
    );
    setCarouselState(0, false);
}

document.querySelectorAll('.js-promotionalQuizzesContainer').forEach(initializeQuizArea);
